// NPM Imports
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Components Imports
import CoverpageView from "../CoverpageView";
import DocumentHeaderView from "../DocumentHeaderView";
import BodyView from "../BodyView";
import DocumentFooterView from "../DocumentFooterView";
import { UpgradePopUp } from "../../../commonComponents";

// Actions Imports
import {
  toggleComponentLockUnlock,
  executeCommand,
  reOrderDocumentSectionList,
  updateDraggedSection,
  getStripePaymentOnSave,
  forceUpdateEditorSectionIdAction,
} from "../../../actions/DocumentActions";
import { EditorActions } from "../../../actions";

// Internal Imports
import { DOCUMENTS, FEATURES } from "../../../constants";
import ToggleSectionLockCommand from "../../../commands/toggleSectionLockCommand";

import APPROVE from "../../../assets/Approved.svg";
import ACCEPTED from "../../../assets/Accepted.svg";
import ESIGNED from "../../../assets/eSigned.svg";
import { GlobalUtils, DocumentUtils } from "../../../utils";

// Destructuring
// const { updateTemplateDocSectionLockUnlock } = DocumentTemplateActions;
const { nukeEditorState } = EditorActions;
const { readDataFromLocalStorage } = GlobalUtils;
const { cloneComponent } = DocumentUtils;
const { DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;
const { FEATURES_NAME } = FEATURES;

class DocumentView extends React.Component {
  documentViewRef = React.createRef();

  state = {
    showContentLockUpgradePopup: false,
    draggedPosition: null,
    dragggedOverData: null,
  };

  componentDidMount() {
    const { isLocked, lock } = this.props;
    if (!(isLocked || lock)) {
      document.addEventListener(
        "click",
        this._handleDocumentViewOutsideClick,
        false,
      );
    }
    setTimeout(() => {
      this.props.startFormRender();
    }, 300);
  }

  componentWillUnmount() {
    const { isLocked, lock } = this.props;
    if (!(isLocked || lock)) {
      document.removeEventListener(
        "click",
        this._handleDocumentViewOutsideClick,
        false,
      );
    }
  }

  _onPlaceholderDragEnter = (newDraggedOverData, draggedPosition) => {
    if (this.state.draggedPosition !== draggedPosition) {
      this.setState({ draggedPosition });
    }
    const { dragggedOverData: prevDraggedOverData } = this.state;
    if (
      !prevDraggedOverData ||
      (prevDraggedOverData &&
        (prevDraggedOverData.rowIndex !== newDraggedOverData.rowIndex ||
          prevDraggedOverData.columnId !== newDraggedOverData.columnId))
    ) {
      this.setState({ dragggedOverData: newDraggedOverData });
    }
  };

  _onPlaceholderDragLeave = () => {
    this.setState({ draggedPosition: null, dragggedOverData: null });
  };

  _clearDragDropData = () => {
    this.setState({ draggedPosition: null, dragggedOverData: null });
  };

  _setRefsByType = (type, element) => {
    if (type === "DOCUMENT_SECTION") {
      this.documentSecRef = element;
    }
  };

  _handleDocumentViewOutsideClick = event => {
    const docSectionNode = this.documentSecRef
      ? this.documentSecRef.current
      : null;
    const customToolbarNode = this.props.customToolbarRef.current;
    const customColorDropdownNode = this.props.toolbarCustomColorDropdownRef
      .current;

    const tableColorpickerDropdownNode = this.props.tableColorpickerDropdownRef
      .current;

    const signatureWrapperNode = this.props.signatureWrapperRef
      ? this.props.signatureWrapperRef.current
      : null;
    // ignore clicks on the document section
    if (
      (docSectionNode && docSectionNode.contains(event.target)) ||
      (customToolbarNode && customToolbarNode.contains(event.target)) ||
      (signatureWrapperNode && signatureWrapperNode.contains(event.target)) ||
      (customColorDropdownNode &&
        customColorDropdownNode.contains(event.target)) ||
      (tableColorpickerDropdownNode &&
        tableColorpickerDropdownNode.contains(event.target))
    ) {
      return;
    }
    const {
      clickedActiveComponentId,
      clickedActiveComponentType,
      preventClickedComponentIdUpdate,
    } = this.props;
    if (clickedActiveComponentId) {
      if (clickedActiveComponentType === "Signature") {
        this.props.setSectionDataForActiveSignatureInRightToolPane({});
        this.props.updateDocumentClickedComponentID(null, true, null);
      } else if (clickedActiveComponentType === "StripePayment") {
        this.props.setSectionDataForActivePaymentButtonInRightToolPane({});
        this.props.updateDocumentClickedComponentID(null, true, null);
      } else if (clickedActiveComponentType === "Image") {
        this.props.updateDocumentClickedComponentID(null, true, null);
      } else if (preventClickedComponentIdUpdate === false) {
        this.props.updateDocumentClickedComponentID(null, true, null);
        // Here, we'll have to nuke the editor store and remove the focus
        // from editor in case of Text and Table Component
        // usecase :=> (ex: applying colors from the toolbar's color-picker)
        if (
          clickedActiveComponentType === "HTML" ||
          clickedActiveComponentType === "Table"
        ) {
          this.props.nukeEditorState(true);
          if (this.documentViewRef && this.documentViewRef.current) {
            this.documentViewRef.current.focus({
              preventScroll: true,
            });
          }
        }
      }
    }
  };

  _handleCloneSection = async (section, rowIndex, colIndex, isNewRow) => {
    const clonedComponent = cloneComponent(section, this.props.mode);
    if (clonedComponent) {
      let layoutSection = null;
      if (this.props.components[section.id]) {
        layoutSection = this.props.components[section.id];
      }
      const newRowIndex = rowIndex + 1;
      const dbRowIndex = layoutSection
        ? layoutSection.dbRowIndex + 1
        : rowIndex + 1;
      const newColIndex = 0;
      const dbColIndex = 0;

      if (section.type === "Signature") {
        if (this.props.mode === "TEMPLATE") {
          clonedComponent.data = section.data;
        } else {
          // getting signatories data for the clone
          const { signatories } = this.props;
          const clonedSignatureData = signatories.find(
            elem => elem.component_id === section.id,
          );
          clonedComponent.data = clonedSignatureData.signers;
        }
      } else if (section.type === "StripePayment") {
        const { data, id } = clonedComponent;

        // if amount is assigned then make a new session with cloned button
        if (data.amount !== "" && data.currency.value) {
          const usableAmount = (Math.round(data.amount * 100) / 100).toFixed(2);
          const stripeData = {
            doc_id: this.props.docID,
            currency: data.currency.value.toLowerCase(),
            amount: parseFloat(usableAmount),
            payment_detail: id,
            stripe_user_id: this.props.stripePaymentDetails.stripe_user_id,
          };
          await this.props.getStripePaymentOnSave(stripeData);
        }
      } else if (section.type === "HTML" || section.type === "Table") {
        this.props.forceUpdateEditorSectionIdAction(section.id);
      }

      await this.props.addComponent(
        clonedComponent,
        newRowIndex,
        newColIndex,
        isNewRow,
        dbRowIndex,
        dbColIndex,
      );
    }
    return null;
  };

  _getCoverpageView = () => {
    // get coverpage section
    const coverpageId = Object.keys(this.props.components).find(
      componentId =>
        this.props.components[componentId] &&
        this.props.components[componentId].content.type ===
          DOCUMENT_COMPONENT_TYPES.COVERPAGE,
    );

    // Disable class for CoverPage Document secctions
    let className = "document-coverpage ";
    if (
      this.props.addComponentType === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
    ) {
      className += " disable-component ";
    } else if (
      this.props.addComponentType === DOCUMENT_COMPONENT_TYPES.COVERPAGE
    ) {
      className += " active ";
    }

    return (
      <div className={className}>
        <CoverpageView
          id={coverpageId}
          title="Cover page"
          className=""
          componentId={coverpageId}
          mode={this.props.mode}
          isLocked={this.props.isLocked}
          draggedFrom={this.props.draggedFrom}
          draggedRowIndex={this.props.draggedRowIndex}
          draggedColumnIndex={this.props.draggedColumnIndex}
          isSharedDocument={this.props.isSharedDocument}
          isDocumentLocked={this.props.isDocumentLocked}
          showDocumentDiffView={this.props.showDocumentDiffView}
          draggedSection={this.props.draggedSection}
          addComponent={this.props.addComponent}
          updateComponent={this.props.updateComponent}
          deleteComponent={this.props.deleteComponent}
          updateHoveredSectionId={this.props.updateHoveredSectionId}
          removeActiveComponents={this.props.removeActiveComponents}
          toggleSectionLockUnlock={this.props.handleSectionLockUnlock}
          hoveredActiveComponentId={this.props.hoveredActiveComponentId}
          coverpageThemeAction={this.props.coverpageThemeAction}
          changeToolsView={this.props.changeToolsView}
          // Refs related props
          customToolbarRef={this.props.customToolbarRef}
          setRefsByType={this._setRefsByType}
          toggleComponentLockUnlock={this.props.toggleComponentLockUnlock}
          executeCommand={this.props.executeCommand}
          // Lock/Unlock
          toggleBlockLockUnlock={this._toggleBlockLockUnlock}
          toggleUpgradePopup={this._toggleUpgradePopup}
          // Drag/Drop
          draggedPosition={this.state.draggedPosition}
          dragggedOverData={this.state.dragggedOverData}
          onPlaceholderDragEnter={this._onPlaceholderDragEnter}
          onPlaceholderDragLeave={this._onPlaceholderDragLeave}
          clearDragDropData={this._clearDragDropData}
          updateDraggedSection={this.props.updateDraggedSection}
          isOpen={this.props.isOpen}
          tableColorpickerDropdownRef={this.props.tableColorpickerDropdownRef}
          match={this.props.match}
        />
      </div>
    );
  };

  /**
   * Get the Document Items View based on some conditions -
   * Show the Empty Document Placeholder Image if -
   * 1. there is no drag &
   * 2. there is only empty section placeholder in section list
   * Else - show the Document Component View
   */
  _getBodyView = () => {
    let className = "document-body ";
    if (
      this.props.addComponentType === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER ||
      this.props.addComponentType === DOCUMENT_COMPONENT_TYPES.COVERPAGE
    ) {
      className += " disable-component ";
    }
    return (
      <div className={className}>
        <BodyView
          id="body"
          mode={this.props.mode}
          layout={this.props.layout}
          components={this.props.components}
          isLocked={this.props.isLocked}
          isSharedDocument={this.props.isSharedDocument}
          isDocumentLocked={this.props.isDocumentLocked}
          draggedSection={this.props.draggedSection}
          draggedFrom={this.props.draggedFrom}
          draggedRowIndex={this.props.draggedRowIndex}
          draggedColumnIndex={this.props.draggedColumnIndex}
          showDocumentDiffView={this.props.showDocumentDiffView}
          addComponent={this.props.addComponent}
          updateComponent={this.props.updateComponent}
          deleteComponent={this.props.deleteComponent}
          cloneSection={this._handleCloneSection}
          reOrderSectionListAction={this.props.reOrderSectionListAction}
          updateDraggedSection={this.props.updateDraggedSection}
          removeHighlightSelection={this.props.removeHighlightSelection}
          updateHoveredSectionId={this.props.updateHoveredSectionId}
          removeActiveComponents={this.props.removeActiveComponents}
          toggleSectionLockUnlock={this.props.handleSectionLockUnlock}
          hoveredActiveComponentId={this.props.hoveredActiveComponentId}
          // Refs related props
          customToolbarRef={this.props.customToolbarRef}
          setRefsByType={this._setRefsByType}
          // Template related props
          toggleComponentLockUnlock={this.props.toggleComponentLockUnlock}
          executeCommand={this.props.executeCommand}
          // Drag/Drop
          draggedPosition={this.state.draggedPosition}
          dragggedOverData={this.state.dragggedOverData}
          onPlaceholderDragEnter={this._onPlaceholderDragEnter}
          onPlaceholderDragLeave={this._onPlaceholderDragLeave}
          clearDragDropData={this._clearDragDropData}
          tableColorpickerDropdownRef={this.props.tableColorpickerDropdownRef}
          match={this.props.match}
        />
      </div>
    );
  };

  _getHeaderView = () => {
    // get header section
    const headerfooterId = Object.keys(this.props.components).find(
      componentId =>
        this.props.components[componentId] &&
        this.props.components[componentId].content.type ===
          DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER,
    );

    let className = "document-header ";
    let isActiveHeader = false;
    if (!this.props.isLocked) {
      if (
        this.props.addComponentType === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
      ) {
        className += " active ";
        isActiveHeader = true;
      } else {
        className += " inactive ";
      }
    } else {
      className += " locked-header-footer ";
    }
    return (
      <div className={className} ref={this.props.docHeaderRef}>
        <DocumentHeaderView
          id={`header${headerfooterId ? `-${headerfooterId}` : ""}`}
          className=""
          componentId={headerfooterId}
          mode={this.props.mode}
          isLocked={this.props.isLocked}
          addComponentType={this.props.addComponentType}
          draggedFrom={this.props.draggedFrom}
          draggedSection={this.props.draggedSection}
          draggedRowIndex={this.props.draggedRowIndex}
          draggedColumnIndex={this.props.draggedColumnIndex}
          isActiveHeaderFooter={isActiveHeader}
          // setDraggedSection={this.props.setDraggedSection}
          updateHoveredSectionId={this.props.updateHoveredSectionId}
          isSharedDocument={this.props.isSharedDocument}
          showDocumentDiffView={this.props.showDocumentDiffView}
          updateAddComponentinToolbarAction={
            this.props.updateAddComponentinToolbarAction
          }
          updateToolPaneData={this.props.updateToolPaneData}
          addComponent={this.props.addComponent}
          updateComponent={this.props.updateComponent}
          deleteComponent={this.props.deleteComponent}
          // Refs related props
          customToolbarRef={this.props.customToolbarRef}
          setRefsByType={this._setRefsByType}
          toggleComponentLockUnlock={this.props.toggleComponentLockUnlock}
          executeCommand={this.props.executeCommand}
          // Lock/Unlock
          toggleBlockLockUnlock={this._toggleBlockLockUnlock}
          toggleUpgradePopup={this._toggleUpgradePopup}
          // Drag/Drop
          draggedPosition={this.state.draggedPosition}
          dragggedOverData={this.state.dragggedOverData}
          onPlaceholderDragEnter={this._onPlaceholderDragEnter}
          onPlaceholderDragLeave={this._onPlaceholderDragLeave}
          clearDragDropData={this._clearDragDropData}
          updateDraggedSection={this.props.updateDraggedSection}
          tableColorpickerDropdownRef={this.props.tableColorpickerDropdownRef}
          match={this.props.match}
        />
      </div>
    );
  };

  _getFooterView = () => {
    // get footer section
    const headerfooterId = Object.keys(this.props.components).find(
      componentId =>
        this.props.components[componentId] &&
        this.props.components[componentId].content.type ===
          DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER,
    );

    let className = "document-footer ";
    let isActiveFooter = false;
    if (!this.props.isLocked) {
      if (
        this.props.addComponentType === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER
      ) {
        className += " active ";
        isActiveFooter = true;
      } else {
        className += " inactive ";
      }
    } else {
      className += " locked-header-footer ";
    }

    return (
      <div className={className} ref={this.props.docFooterRef}>
        <DocumentFooterView
          id={`footer${headerfooterId ? `-${headerfooterId}` : ""}`}
          className=""
          componentId={headerfooterId}
          mode={this.props.mode}
          isLocked={this.props.isLocked}
          draggedFrom={this.props.draggedFrom}
          draggedSection={this.props.draggedSection}
          draggedRowIndex={this.props.draggedRowIndex}
          draggedColumnIndex={this.props.draggedColumnIndex}
          isActiveHeaderFooter={isActiveFooter}
          // setDraggedSection={this.props.setDraggedSection}
          updateHoveredSectionId={this.props.updateHoveredSectionId}
          isSharedDocument={this.props.isSharedDocument}
          showDocumentDiffView={this.props.showDocumentDiffView}
          updateAddComponentinToolbarAction={
            this.props.updateAddComponentinToolbarAction
          }
          updateToolPaneData={this.props.updateToolPaneData}
          addComponent={this.props.addComponent}
          updateComponent={this.props.updateComponent}
          deleteComponent={this.props.deleteComponent}
          addComponentType={this.props.addComponentType}
          // Refs related props
          customToolbarRef={this.props.customToolbarRef}
          setRefsByType={this._setRefsByType}
          toggleComponentLockUnlock={this.props.toggleComponentLockUnlock}
          executeCommand={this.props.executeCommand}
          // Lock/Unlock
          toggleBlockLockUnlock={this._toggleBlockLockUnlock}
          toggleUpgradePopup={this._toggleUpgradePopup}
          // Drag/Drop
          draggedPosition={this.state.draggedPosition}
          dragggedOverData={this.state.dragggedOverData}
          onPlaceholderDragEnter={this._onPlaceholderDragEnter}
          clearDragDropData={this._clearDragDropData}
          updateDraggedSection={this.props.updateDraggedSection}
          tableColorpickerDropdownRef={this.props.tableColorpickerDropdownRef}
          match={this.props.match}
        />
      </div>
    );
  };

  _documentStatusBand = () => {
    const CURRENT_USER = readDataFromLocalStorage("revvsalesUser");
    const { stage, docRecipientList, acceptorsList } = this.props;
    const signerList = [];
    docRecipientList.map(item =>
      item.recipient_type === "SIGNER" ? signerList.push(item) : null,
    );
    let bandStatus = null;
    if (
      CURRENT_USER &&
      this.props.mode === "DOCUMENT" &&
      !this.props.isSharedDocument
    ) {
      if (stage === "INTERNAL_REVIEW") {
        if (this.props.pendingApprovalRequired) {
          bandStatus = (
            <div className="doc-status-band remove-from-export">
              <span className="doc-status-internal-check">
                <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
              </span>

              <div> Your approval is required on this document</div>
            </div>
          );
        } else if (
          this.props.docApprovalStatus[0] &&
          this.props.docApprovalStatus[0].workflowStatus === "REJECTED"
        ) {
          bandStatus = (
            <div className="doc-declined-status-band remove-from-export">
              <span className="doc-statusband-check-wrapper">
                <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
              </span>

              <div>Approval request on document rejected</div>
            </div>
          );
        } else if (
          this.props.docApprovalStatus[0] &&
          this.props.docApprovalStatus[0].workflowStatus === "PENDING"
        ) {
          bandStatus = (
            <div className="doc-status-warning-band remove-from-export">
              <span className="doc-statusband-check-wrapper">
                <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
              </span>

              <div>Document sent for internal approval</div>
            </div>
          );
        }
      } else if (stage === "APPROVED") {
        if (
          this.props.docApprovalStatus &&
          this.props.docApprovalStatus.length > 0
        ) {
          bandStatus = (
            <div className="doc-status-band remove-from-export">
              <img
                src={APPROVE}
                className="doc-imgband-check-wrapper"
                alt="Revv"
              />

              <div>Everyone approved the document</div>
            </div>
          );
        } else {
          bandStatus = (
            <div className="doc-status-band remove-from-export">
              <img
                src={APPROVE}
                className="doc-imgband-check-wrapper"
                alt="Revv"
              />

              <div>
                Document did not trigger any workflow. It is auto-approved
              </div>
            </div>
          );
        }
      } else if (stage === "SENT") {
        if (acceptorsList && acceptorsList.length > 0) {
          if (
            acceptorsList &&
            acceptorsList.find(
              item =>
                item.user &&
                item.user.id === CURRENT_USER.id &&
                item.recipient_type === "ACCEPTOR" &&
                item.accepting_states === "",
            )
          ) {
            bandStatus = (
              <div className="doc-status-warning-band remove-from-export">
                <span className="doc-statusband-check-wrapper">
                  <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
                </span>

                <div>Your consent is required on this document</div>
              </div>
            );
          } else {
            bandStatus = (
              <div className="doc-status-warning-band remove-from-export">
                <span className="doc-statusband-check-wrapper">
                  <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
                </span>

                <div>Document sent for acceptance</div>
              </div>
            );
          }
        } else if (signerList && signerList.length > 0) {
          if (
            docRecipientList &&
            docRecipientList.find(
              item =>
                item.user &&
                item.user.id === CURRENT_USER.id &&
                item.recipient_type === "SIGNER",
            )
          ) {
            bandStatus = (
              <div className="doc-status-warning-band remove-from-export">
                <span className="doc-statusband-check-wrapper">
                  <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
                </span>

                <div>Document requires your consent.</div>
              </div>
            );
          } else {
            bandStatus = (
              <div className="doc-status-warning-band remove-from-export">
                <span className="doc-statusband-check-wrapper">
                  <FontAwesomeIcon icon={["far", "signature"]} />
                </span>

                <div>Document sent for eSignature</div>
              </div>
            );
          }
        }
      } else if (stage === "COMPLETED") {
        if (acceptorsList && acceptorsList.length > 0) {
          bandStatus = (
            <div className="doc-status-band remove-from-export">
              <img
                src={ACCEPTED}
                className="doc-imgband-check-wrapper"
                alt="Revv"
              />
              <div>Everyone accepted the document</div>
            </div>
          );
        } else if (signerList && signerList.length > 0) {
          bandStatus = (
            <div className="doc-esigncompleted-status-band remove-from-export">
              <img
                src={ESIGNED}
                className="doc-esign-imgband-check"
                alt="Revv"
              />

              <div>Everyone signed the document</div>
            </div>
          );
        }
      } else if (stage === "DECLINED") {
        if (acceptorsList && acceptorsList.length > 0) {
          bandStatus = (
            <div className="doc-declined-status-band remove-from-export">
              <span className="doc-statusband-check-wrapper">
                <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
              </span>
              <div>Document has been rejected by a receiver</div>
            </div>
          );
        } else if (signerList && signerList.length > 0) {
          bandStatus = (
            <div className="doc-declined-status-band remove-from-export">
              <span className="doc-statusband-check-wrapper">
                <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
              </span>

              <div>Document declined by a signer</div>
            </div>
          );
        }
      }
    }
    return bandStatus;
  };

  _acceptanceStatusBand = () => {
    const { stage } = this.props;
    let bandStatus = null;
    if (this.props.isSharedDocument) {
      if (this.props.recipientType === "ACCEPTOR" && stage === "COMPLETED") {
        bandStatus = (
          <div className="doc-status-band remove-from-export">
            <img
              src={ACCEPTED}
              className="doc-imgband-check-wrapper"
              alt="Revv"
            />
            <div>Everyone accepted the document</div>
          </div>
        );
      } else if (
        this.props.recipientType === "ACCEPTOR" &&
        stage === "DECLINED"
      ) {
        bandStatus = (
          <div className="doc-declined-status-band remove-from-export">
            <span className="doc-statusband-check-wrapper">
              <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
            </span>
            <div>Document was rejected</div>
          </div>
        );
      } else if (
        this.props.acceptingStates === "REJECTED" ||
        this.state.docAcceptingState === "REJECTED"
      ) {
        bandStatus = (
          <div className="doc-declined-status-band remove-from-export">
            <span className="doc-statusband-check-wrapper">
              <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
            </span>
            Document was rejected
          </div>
        );
      } else if (
        this.props.acceptingStates === "ACCEPTED" ||
        this.state.docAcceptingState === "ACCEPTED"
      ) {
        bandStatus = (
          <div className="doc-status-band remove-from-export">
            <span className="doc-statusband-check-accepted">
              <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
            </span>
            <div>You have accepted this document</div>
          </div>
        );
      } else if (
        this.props.recipientType === "ACCEPTOR" &&
        this.props.acceptingStates === ""
      ) {
        bandStatus = (
          <div className="doc-status-warning-band remove-from-export">
            <span className="doc-statusband-check-wrapper">
              <FontAwesomeIcon icon={["far", "exclamation-circle"]} />
            </span>
            <div>Your consent is required on this document</div>
          </div>
        );
      }
    }

    return bandStatus;
  };

  _toggleBlockLockUnlock = (blockContent, lockStatus) => {
    const toggelSectionLockCommand = new ToggleSectionLockCommand({
      blockContent,
      lockStatus,
      updateComponent: this.props.updateComponent,
      updateDocumentClickedComponentID: this.props
        .updateDocumentClickedComponentID,
      updateHoveredSectionId: this.props.updateHoveredSectionId,
      nukeEditorState: this.props.nukeEditorState,
    });
    this.props.executeCommand(toggelSectionLockCommand);
  };

  _toggleUpgradePopup = (type, flag) => {
    this.setState({ [type]: flag });
  };

  render() {
    // This is for upgrade popups of ConentLock Option
    const { showContentLockUpgradePopup } = this.state;
    let contentUpgradePopup = null;
    let featureName = "";
    let decideType = "";
    if (showContentLockUpgradePopup) {
      featureName = FEATURES_NAME.DOCUMENTS_CONTENT_LOCKING;
      decideType = "showContentLockUpgradePopup";
    }
    if (showContentLockUpgradePopup) {
      contentUpgradePopup = (
        <UpgradePopUp
          featureName={featureName}
          onClose={() => this._toggleUpgradePopup(decideType, false)}
        />
      );
    }
    return (
      <Fragment>
        {contentUpgradePopup}
        <div
          className={` document-view-main-container
                ${!this.props.show ? "document-view-visiblity-hidden" : ""}
              `}>
          <div
            className="doc-wrap-container"
            ref={this.documentViewRef}
            role="button"
            tabIndex="0">
            <div
              className="document-view-container document-conatiner"
              id="document-view-container">
              {this._getCoverpageView()}
              <div className="document-sections-container">
                {/* -------------- Header Section -------------- */}
                {this._getHeaderView()}
                {/* -------------- Normal Document Section -------------- */}
                {this._getBodyView()}
                {/* -------------- Footer Section -------------- */}
                {this._getFooterView()}
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = state => {
  const {
    document: { showForm, created_by: createdBy, signatories } = {},
    draggedFrom,
    draggedRowIndex,
    draggedColumnIndex,
    stripePaymentDetails,
    components,
  } = state.DocumentStore;

  const { isOpen } = state.ToolPaneStore;

  const { stripePaymentStatus } = state.DocumentStore;
  return {
    showForm,
    createdBy,
    stripePaymentStatus,
    isOpen,
    draggedFrom,
    draggedRowIndex,
    draggedColumnIndex,
    signatories,
    stripePaymentDetails,
    components,
  };
};

const mapDispatchToProps = {
  toggleComponentLockUnlock,
  nukeEditorState,
  executeCommand,
  reOrderSectionListAction: reOrderDocumentSectionList,
  updateDraggedSection,
  getStripePaymentOnSave,
  forceUpdateEditorSectionIdAction,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(DocumentView);



// WEBPACK FOOTER //
// ./src/components/DocumentV2/DocumentView/DocumentView.js